import { useAuthContext } from "../context/AuthContext";
import { IAuthenticatedUser } from "../models/authenticated-user.interface";
import { Permissions } from "../models/permission";
import Role from "../models/role";
import { OrgType } from "../models/users.interface";
import authorizationService from "../services/authorization.service";

export enum AuthorizedActionsKeys {
  ViewEcgGraphAndData = "viewEcgGraphAndData",
  EditInterpretation = "editInterpretation",
  ReviewEcg = "reviewEcg",
  ApproveEcg = "approveEcg",
  ViewAsCroOrg = "viewAsCroOrg",
  ViewAsCroExternOrg = "viewAsCroExternOrg",
  ViewAsProfessional = "viewAsProfessional",
}

type AuthorizedAction = {
  roles?: Role[];
  permissions?: Permissions[];
  orgType?: OrgType[];
};

type AuthorizedActionFunc = (user: IAuthenticatedUser) => AuthorizedAction;

type AuthorizedActions = {
  [AuthorizedActionsKeys.ViewEcgGraphAndData]: AuthorizedAction;
  [AuthorizedActionsKeys.EditInterpretation]: AuthorizedActionFunc;
  [AuthorizedActionsKeys.ApproveEcg]: AuthorizedAction;
  [AuthorizedActionsKeys.ReviewEcg]: AuthorizedAction;
  [AuthorizedActionsKeys.ViewAsCroOrg]: AuthorizedAction;
  [AuthorizedActionsKeys.ViewAsCroExternOrg]: AuthorizedAction;
  [AuthorizedActionsKeys.ViewAsProfessional]: AuthorizedAction;
};

const authorizedActions: AuthorizedActions = {
  viewEcgGraphAndData: {
    roles: [
      Role.Professional,
      Role.OrgProfessional,
      Role.OrgAdmin,
      Role.Reviewer,
      Role.Consultant,
    ],
    permissions: [Permissions.CanViewEcgs],
  },
  editInterpretation: (user) => {
    const permissions = [Permissions.CanViewEcgs];

    if (user.role === Role.OrgProfessional) {
      permissions.push(Permissions.CanEditInterpretation);
    }

    return {
      roles: [
        Role.Professional,
        Role.OrgProfessional,
        Role.Reviewer,
        Role.Consultant,
      ],
      permissions,
    };
  },
  reviewEcg: {
    roles: [Role.Reviewer],
  },
  viewAsCroOrg: {
    orgType: [OrgType.CRO],
  },
  viewAsCroExternOrg: {
    orgType: [OrgType.CroExtern],
  },
  approveEcg: {
    permissions: [Permissions.CanViewEcgs, Permissions.CanApproveEcg],
    orgType: [OrgType.CRO, OrgType.CroExtern],
  },
  viewAsProfessional: {
    roles: [Role.Professional],
  },
};

const useAuthorizedAction = (actionKey: AuthorizedActionsKeys) => {
  const {
    state: { user },
  } = useAuthContext();

  if (!user)
    throw new Error(
      `${useAuthorizedAction.name} Authorized user does not exist`
    );

  const authorizedAction = authorizedActions[actionKey];

  if (!authorizedAction)
    throw new Error(
      `${useAuthorizedAction.name} Invalid Authorized actions ${actionKey}`
    );

  let action =
    typeof authorizedAction === "function"
      ? authorizedAction(user)
      : authorizedAction;

  if (!action.roles && !action.permissions && !action.orgType) return true;

  let isAuthorized = true;

  if (action.orgType) {
    if (typeof user.organization?.type !== "undefined") {
      isAuthorized = action.orgType.includes(user.organization?.type);
    }
  }

  if (isAuthorized && action.roles) {
    isAuthorized = authorizationService.hasRole(user.role, action.roles);
  }

  if (isAuthorized && action.permissions) {
    isAuthorized = authorizationService.hasPermissions(
      user.permissions,
      action.permissions
    );
  }
  return isAuthorized;
};

export default useAuthorizedAction;
